import { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { APP_VERSION } from "../version";
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, Smartphone, Users, Wrench } from "lucide-react";
import { useBrand } from "../context/BrandContext";
import StaffLoading from "../components/StaffLoading";
import "./Login.css";

export default function Login() {
  const { user, ready, allowed, logout, login, claimsReady, profileReady } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const { logos } = useBrand();

  // Access request state (no auto-account creation)
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqNote, setReqNote] = useState("");
  const [reqDone, setReqDone] = useState(false);

  if (!ready || signingIn || (user && (!claimsReady || !profileReady))) return <StaffLoading />;
  if (user && allowed) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (signingIn) return;
    setErr("");
    setSigningIn(true);
    try {
      await login(email, pass);
    } catch (e) {
      setErr(e.message || "Failed");
    } finally {
      setSigningIn(false);
    }
  };

  const submitAccessRequest = async (e) => {
    e.preventDefault();
    if (requestBusy) return;
    setErr("");
    setRequestBusy(true);
    try {
      const name = reqName.trim();
      const em = reqEmail.trim() || email.trim();
      if (!em) { setErr("Please enter your email"); return; }
      await addDoc(collection(db, "accessRequests"), {
        name: name || null,
        email: em,
        note: (reqNote || "").trim() || null,
        createdAt: serverTimestamp(),
        status: "PENDING",
      });
      setReqDone(true);
    } catch (e) {
      setErr(e.message || "Failed to submit request");
    } finally {
      setRequestBusy(false);
    }
  };

  const denied = new URLSearchParams(loc.search).get("denied");
  const authorizationChecked = claimsReady || profileReady;

  return (
    <main className="staff-login">
      <div className="staff-login-shell">
        <section className="staff-login-story" aria-label="Alkeya staff workspace">
          <div className="staff-login-brand">
            <div className={`staff-login-identity${logos?.login || logos?.app ? "" : " staff-login-identity-bundled"}`}>
              <img src={logos?.login || logos?.app || "/logo.jpg"} alt="Alkeya Mobile" />
            </div>
            <span className="staff-login-wordmark" aria-hidden="true">alkeya<span>.</span></span>
            <span>TEAM WORKSPACE</span>
          </div>
          <div className="staff-login-intro">
            <span className="staff-login-eyebrow"><span /> Built for the people behind Alkeya</span>
            <h1>Your team.<br />Your tools.<br /><span>One connected space.</span></h1>
            <p>From the first hello to the final fix. Everything you need to keep your customers moving.</p>
            <div className="staff-login-tools">
              <span><Users aria-hidden="true" /> Customers</span>
              <span><Smartphone aria-hidden="true" /> Wireless</span>
              <span><Wrench aria-hidden="true" /> Repairs</span>
            </div>
          </div>
          <div className="staff-login-story-footer"><ShieldCheck aria-hidden="true" /> Employee access. Customer-first service.</div>
        </section>
        <section className="staff-login-panel" aria-labelledby="staff-sign-in-title">
          <header className="staff-login-heading">
            <span className="staff-login-lock"><Lock aria-hidden="true" /></span>
            <span className="staff-login-eyebrow">LET&apos;S GET TO WORK</span>
            <h2 id="staff-sign-in-title">Welcome back.</h2>
            <p>Sign in to your Alkeya staff account.</p>
          </header>
        <form onSubmit={submit} className="staff-login-form" aria-busy={signingIn}>
        {ready && user && !allowed && authorizationChecked && (
          <div role="alert" className="staff-login-notice">
            Firebase accepted your sign-in, but this account is not authorized for the app. Ask an owner or admin to check that your user profile is active and has an allowed role (admin, owner, staff, or viewer), then sign out and back in.
          </div>
        )}
        {denied && (
          <div role="alert" className="staff-login-notice">
            Your account exists but is not authorized yet. Please request access below or contact an admin.
          </div>
        )}
        <label htmlFor="staff-email">Work email</label>
        <div className="staff-login-input">
        <Mail aria-hidden="true" />
        <input id="staff-email" name="email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required
          placeholder="you@alkeyamobile.com"
          value={email}
          onChange={e=>setEmail(e.target.value)}
        />
        </div>
        <label htmlFor="staff-password">Password</label>
        <div className="staff-login-input">
        <Lock aria-hidden="true" />
        <input id="staff-password" name="password" autoComplete="current-password" required
          placeholder="Enter your password"
          type={showPassword ? "text" : "password"}
          value={pass}
          onChange={e=>setPass(e.target.value)}
        />
        <button type="button" className="staff-login-reveal" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword(v => !v)}>
          {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
        </button>
        </div>
        {err && <div role="alert" className="staff-login-error">{err}</div>}
        <button disabled={signingIn} className="staff-login-primary" type="submit">
          {signingIn ? "Signing in..." : "Sign in"} <ArrowRight aria-hidden="true" />
        </button>
        {user && !allowed && (
          <button type="button" className="staff-login-secondary" onClick={()=>logout && logout()}>
            Sign out and switch account
          </button>
        )}
        </form>
        <div className="staff-login-access">
          New to the team? <button type="button" aria-expanded={requestOpen} aria-controls="staff-access-request" onClick={() => { if (!requestOpen && !reqEmail) setReqEmail(email.trim()); setRequestOpen(v => !v); }}>Request access <ArrowRight aria-hidden="true" /></button>
        </div>
        {requestOpen && (
          <div className="staff-login-request" id="staff-access-request">
            {reqDone ? (
              <div role="status" className="staff-login-success">Request submitted. An admin will review and enable your access.</div>
            ) : (
              <form onSubmit={submitAccessRequest} className="staff-login-form" aria-busy={requestBusy}>
                <h3>Join your team</h3>
                <p>An administrator will review your request. This does not create an account automatically.</p>
                <label htmlFor="staff-request-name">Name (optional)</label>
                <input id="staff-request-name" autoComplete="name" value={reqName} onChange={e=>setReqName(e.target.value)} />
                <label htmlFor="staff-request-email">Work email</label>
                <input id="staff-request-email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required value={reqEmail} placeholder="you@alkeyamobile.com" onChange={e=>setReqEmail(e.target.value)} />
                <label htmlFor="staff-request-note">Tell us who you are (optional)</label>
                <textarea id="staff-request-note" rows={2} value={reqNote} onChange={e=>setReqNote(e.target.value)} />
                <button className="staff-login-secondary" disabled={requestBusy} type="submit">{requestBusy ? "Submitting..." : "Submit request"}</button>
              </form>
            )}
          </div>
        )}

        <div className="staff-login-help"><ShieldCheck aria-hidden="true" /><p>For authorized Alkeya team members.<br />Need sign-in help? Contact your administrator.</p></div>
        <footer className="staff-login-footer">
          <span>Alkeya Mobile</span><span>{APP_VERSION}</span>
        </footer>
        </section>
        </div>
    </main>
  );
}
